import Link from '@/lib/compat/link';
import { usePathname } from '@/lib/compat/navigation';
import { cn } from '@/lib/utils';
import { MoreHorizontal } from 'lucide-react';
import { isMainNavActive, mobileBottomNavItems } from '@/config/navigation';
import { useTranslations } from '@/lib/i18n/client';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useOrderActionCounts } from '@/lib/hooks/use-order-action-counts';

interface MobileNavProps {
  onMoreClick?: () => void;
}

export function MobileNav({ onMoreClick }: MobileNavProps) {
  const pathname = usePathname();
  const t = useTranslations();
  const { hasPermission } = usePermissions();
  const { ordersBadge } = useOrderActionCounts();

  const visibleItems = mobileBottomNavItems.filter(
    (i) => !i.requiredPermission || hasPermission(i.requiredPermission)
  );

  return (
    <nav className="fixed inset-x-3 bottom-3 z-50 rounded-2xl border border-border bg-card/95  backdrop-blur-xl pb-safe lg:hidden">
      <div className="flex h-16 items-center justify-around px-1">
        {visibleItems.map((item) => {
          const isActive = isMainNavActive(pathname, item.href);
          const Icon = item.icon;

          return (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                'relative flex min-w-16 flex-col items-center justify-center gap-1 rounded-xl px-3 py-2 transition-colors',
                isActive
                  ? 'bg-brand-tint text-brand-strong'
                  : 'text-muted-foreground'
              )}
            >
              <div className="relative">
                <Icon className="size-5" />
                {item.href === '/orders' && ordersBadge > 0 && (
                  <span className="absolute -top-1.5 -end-2 flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold text-white bg-red-500 rounded-full">
                    {ordersBadge > 9 ? '9+' : ordersBadge}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium">{t(item.labelKey)}</span>
            </Link>
          );
        })}
        {/* More button */}
        <button
          onClick={onMoreClick}
          className="flex min-w-16 flex-col items-center justify-center gap-1 rounded-xl px-3 py-2 text-muted-foreground transition-colors hover:bg-accent"
        >
          <MoreHorizontal className="size-5" />
          <span className="text-[10px] font-medium">{t('common.more')}</span>
        </button>
      </div>
    </nav>
  );
}
